import { useEffect } from "react";
import { Editor, TLShape, TLShapeId } from "@tldraw/tldraw";

/**
 * Hook to sync mask group position when the mask shape is moved
 * This ensures that when entering edit mode, the original shapes appear
 * at the correct position relative to the mask shape
 */
export function useMaskShapeSync(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;

    // Listen to store changes
    const unsubscribe = editor.store.listen(
      ({ changes }) => {
        // Only process updates
        if (!changes.updated) return;

        // Check each updated shape
        for (const [_id, [fromRecord, toRecord]] of Object.entries(changes.updated)) {
          const fromShape = fromRecord as TLShape;
          const toShape = toRecord as TLShape;

          // Check if this is a mask shape that moved
          if (toShape.meta?.isMaskShape && (fromShape.x !== toShape.x || fromShape.y !== toShape.y)) {
            const deltaX = toShape.x - fromShape.x;
            const deltaY = toShape.y - fromShape.y;

            // Get the associated mask group
            const groupId = toShape.meta.maskGroupId as TLShapeId;
            if (!groupId) {
              console.warn("  ⚠️ No mask group ID found in mask shape metadata");
              continue;
            }

            const maskGroup = editor.getShape(groupId);
            if (!maskGroup || maskGroup.type !== "group") {
              console.warn("  ⚠️ Mask group not found:", groupId);
              continue;
            }

            // Move the entire mask group by the same delta
            editor.updateShape({
              id: groupId,
              type: "group",
              x: maskGroup.x + deltaX,
              y: maskGroup.y + deltaY,
            });
          }
        }
      },
      { source: "user", scope: "document" }
    );

    return () => {
      unsubscribe();
    };
  }, [editor]);
}
